// src/pages/AcceptInvite.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, LogOut, UserPlus, ArrowRight, LogIn } from "lucide-react";
import i18n from "../i18n";

function useI18nSafe() {
  const [lng, setLng] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  // stable par langue : sinon `t` change à chaque rendu et relance les useEffect en boucle
  const t = useCallback(
    (key: string, options?: Record<string, unknown>): string =>
      options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lng]
  );
  return { t, lng };
}

type UiState = "loading" | "need-auth" | "ok" | "error";

export default function AcceptInvite() {
  const { t } = useI18nSafe();
  const nav = useNavigate();
  const { search } = useLocation();

  const [state, setState] = useState<UiState>("loading");
  const [message, setMessage] = useState<string>("");
  const [hint, setHint] = useState<"email_mismatch" | "generic" | null>(null);

  const params = useMemo(() => new URLSearchParams(search), [search]);
  const token = params.get("token") || "";

  // Utilitaires de redirection avec conservation du "redirect" (même convention que Signup/Login/AuthCallback)
  const returnTo = useMemo(() => `/accept-invite?token=${encodeURIComponent(token)}`, [token]);

  const goSignup = () => {
    try { localStorage.setItem("pendingInviteToken", token); } catch {}
    nav(`/signup?redirect=${encodeURIComponent(returnTo)}`);
  };

  const goLogin = () => {
    try { localStorage.setItem("pendingInviteToken", token); } catch {}
    nav(`/login?redirect=${encodeURIComponent(returnTo)}`);
  };

  const switchAccount = async () => {
    await supabase.auth.signOut();
    goLogin();
  };

  useEffect(() => {
    // 0) Token requis
    if (!token) {
      setState("error");
      setMessage(t("invite:missing_token"));
      setHint("generic");
      return;
    }

    (async () => {
      setState("loading");
      setHint(null);

      // 1) Session ?
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // Pas de session -> on laisse le choix entre se connecter (compte existant) ou créer un compte
        try { localStorage.setItem("pendingInviteToken", token); } catch {}
        setState("need-auth");
        return;
      }

      // 2) Tenter le claim
      const { error } = await supabase.rpc("claim_team_invite", { p_token: token });

      if (error) {
        const msg = (error as any)?.message?.toString?.() || "";
        // Heuristiques propres pour améliorer l'UX sans toucher l'RPC:
        // - messages d'erreur courants côté SQL/PLPGSQL à détecter
        if (/mismatch|different email|other account/i.test(msg)) {
          setState("error");
          setHint("email_mismatch");
          setMessage(t("invite:email_mismatch", { email: user.email || "" }));
          return;
        }
        if (/expired|already used|invalid|not found/i.test(msg)) {
          setState("error");
          setHint("generic");
          setMessage(t("invite:invalid_or_used"));
          return;
        }
        // défaut
        setState("error");
        setHint("generic");
        setMessage(msg || t("invite:invalid_or_used"));
        return;
      }

      // 3) Succès
      setState("ok");
      setMessage(t("invite:accepted"));
      // Petite pause d'UX, puis redirection
      const id = setTimeout(() => nav("/myteam"), 1200);
      return () => clearTimeout(id);
    })();
  }, [token, t, nav, returnTo]);

  return (
    <div className="max-w-md mx-auto mt-16 p-6 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
      {/* LOADING */}
      {state === "loading" && (
        <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>{t("invite:claiming")}</span>
        </div>
      )}

      {/* NEED AUTH */}
      {state === "need-auth" && (
        <div className="space-y-4 text-center">
          <div className="flex flex-col items-center gap-2 text-gray-700 dark:text-gray-300">
            <LogIn className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            <span>{t("invite:auth_required")}</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              onClick={goLogin}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white"
            >
              <LogIn className="w-4 h-4" />
              {t("invite:go_login")}
            </button>
            <button
              onClick={goSignup}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-gray-300 dark:border-gray-700"
            >
              <UserPlus className="w-4 h-4" />
              {t("invite:create_account")}
            </button>
          </div>
        </div>
      )}

      {/* OK */}
      {state === "ok" && (
        <div className="flex items-center gap-3 text-green-700 dark:text-green-300">
          <CheckCircle2 className="w-6 h-6" />
          <span>{message}</span>
        </div>
      )}

      {/* ERROR */}
      {state === "error" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-amber-700 dark:text-amber-300">
            <AlertTriangle className="w-6 h-6" />
            <span>{message}</span>
          </div>

          {/* Suggestions d'action en fonction de l'erreur */}
          <div className="flex flex-col sm:flex-row gap-2">
            {hint === "email_mismatch" ? (
              <>
                <button
                  onClick={switchAccount}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <LogOut className="w-4 h-4" />
                  {t("invite:switch_account")}
                </button>
                <button
                  onClick={goSignup}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-gray-300 dark:border-gray-700"
                >
                  <UserPlus className="w-4 h-4" />
                  {t("invite:create_account")}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={goLogin}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <ArrowRight className="w-4 h-4" />
                  {t("invite:go_login")}
                </button>
                <button
                  onClick={goSignup}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-gray-300 dark:border-gray-700"
                >
                  <UserPlus className="w-4 h-4" />
                  {t("invite:create_account")}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
